export interface ProductPdfFile {
  label: string;
  translatedLabel?: string;
  url?: string;
  fileName?: string;
  s3Key?: string;
  sortOrder: number;
  file?: File;
  isNew?: boolean;
  markedForDelete?: boolean;
  isLegacy?: boolean;
}

export interface ProductPdfFilePayload {
  label: string;
  url: string;
  fileName?: string;
  s3Key?: string;
  sortOrder: number;
}

export interface ProductPdfValidationResult {
  valid: boolean;
  message?: string;
}

export const PDF_MAX_SIZE_MB = 5;
export const PDF_MAX_SIZE_BYTES = PDF_MAX_SIZE_MB * 1024 * 1024;
export const PRODUCT_FILES_S3_HOST = 'https://mobydyg-files.s3.amazonaws.com/';

export function createEmptyProductPdfFile(sortOrder: number): ProductPdfFile {
  return {
    label: '',
    sortOrder,
    isNew: true,
  };
}

export function extractS3Key(urlOrKey?: string | null): string | undefined {
  if (!urlOrKey) {
    return undefined;
  }

  const value = String(urlOrKey).trim();
  if (!value) {
    return undefined;
  }

  if (value.startsWith(PRODUCT_FILES_S3_HOST)) {
    return value.replace(PRODUCT_FILES_S3_HOST, '');
  }

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      return parsed.pathname.replace(/^\/+/, '') || undefined;
    } catch (error) {
      return undefined;
    }
  }

  return value;
}

export function getFileNameFromUrl(url?: string | null): string | undefined {
  if (!url) {
    return undefined;
  }

  const cleanUrl = String(url).split('?')[0];
  const fileName = cleanUrl.split('/').filter(Boolean).pop();
  return fileName || undefined;
}

export function isProductPdfFile(file: File): boolean {
  return file.type === 'application/pdf';
}

export function parseProductFiles(rawProductFiles: unknown): any[] {
  if (!rawProductFiles) {
    return [];
  }

  if (Array.isArray(rawProductFiles)) {
    return rawProductFiles;
  }

  if (typeof rawProductFiles === 'string') {
    const trimmed = rawProductFiles.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Invalid productFiles JSON payload:', error);
      return [];
    }
  }

  return [];
}

export function normalizeProductFiles(
  product: any,
  legacyLabel: string = 'Ver PDF'
): ProductPdfFile[] {
  const productData = product?.product ?? product ?? {};
  const productFiles = parseProductFiles(productData.productFiles)
    .map((file: any, index: number): ProductPdfFile | null => {
      const url = file?.url ? String(file.url).trim() : '';
      const label = file?.label ? String(file.label).trim() : '';
      const translatedLabel = file?.translatedLabel
        ? String(file.translatedLabel).trim()
        : '';

      if (!url && !label) {
        return null;
      }

      return {
        label,
        ...(translatedLabel ? { translatedLabel } : {}),
        url: url || undefined,
        fileName: file?.fileName || getFileNameFromUrl(url),
        s3Key: file?.s3Key || extractS3Key(url),
        sortOrder: Number.isFinite(Number(file?.sortOrder))
          ? Number(file.sortOrder)
          : index + 1,
        isNew: false,
        isLegacy: false,
      };
    })
    .filter((file): file is ProductPdfFile => !!file)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((file, index) => ({
      ...file,
      sortOrder: index + 1,
    }));

  if (productFiles.length > 0) {
    return productFiles;
  }

  const legacyProductFile = productData.productFile
    ? String(productData.productFile).trim()
    : '';

  if (!legacyProductFile) {
    return [];
  }

  return [
    {
      label: legacyLabel,
      url: legacyProductFile,
      fileName: getFileNameFromUrl(legacyProductFile),
      s3Key: extractS3Key(legacyProductFile),
      sortOrder: 1,
      isNew: false,
      isLegacy: true,
    },
  ];
}

export function validateProductPdfFiles(
  productFiles: ProductPdfFile[]
): ProductPdfValidationResult {
  for (const file of productFiles) {
    if (file.markedForDelete) {
      continue;
    }

    const label = file.label ? file.label.trim() : '';
    const hasFile = !!file.file;
    const hasUrl = !!file.url;

    if (!label && !hasFile && !hasUrl) {
      return {
        valid: false,
        message: 'Complete o elimine las filas de PDF vacias.',
      };
    }

    if (!label && (hasFile || hasUrl)) {
      return {
        valid: false,
        message: 'Cada PDF debe tener una etiqueta.',
      };
    }

    if (label && !hasFile && !hasUrl) {
      return {
        valid: false,
        message: 'Cada etiqueta debe tener un archivo PDF.',
      };
    }

    if (file.file && !isProductPdfFile(file.file)) {
      return {
        valid: false,
        message: 'Solo se permiten archivos PDF.',
      };
    }

    if (file.file && file.file.size > PDF_MAX_SIZE_BYTES) {
      return {
        valid: false,
        message: `Cada PDF debe pesar maximo ${PDF_MAX_SIZE_MB} MB.`,
      };
    }
  }

  return { valid: true };
}

export function buildProductFilesPayload(
  productFiles: ProductPdfFile[]
): ProductPdfFilePayload[] {
  return productFiles
    .filter((file) => !file.markedForDelete)
    .filter((file) => !!file.label?.trim() && !!file.url)
    .map((file, index) => ({
      label: file.label.trim(),
      url: file.url as string,
      fileName: file.fileName || getFileNameFromUrl(file.url),
      s3Key: file.s3Key || extractS3Key(file.url),
      sortOrder: index + 1,
    }));
}
